import {gql} from '@apollo/client';
import * as PropTypes from 'prop-types';

import {PolicyAction, PolicyCondition, PolicyItemType} from 'graphql-client/__generated/graphqlGlobalTypes';
import * as logging from 'util/Logging';

export type Action = PolicyAction;
export const Action = {...PolicyAction};

export type Condition = PolicyCondition;
export const Condition = {...PolicyCondition};

export type ItemType = PolicyItemType;
export const ItemType = {...PolicyItemType};

const CONDITION_TO_DISPLAY_STRING: {[condition in Condition]: string} = {
  [Condition.ALWAYS]: 'Granted',
  [Condition.IS_AUTHOR]: 'Granted to author',
  [Condition.IS_ASSIGNEE]: 'Granted to assignee',
  [Condition.UNKNOWN]: '',
};

export function getConditionDisplayString(condition: Condition): string {
  if (condition === Condition.UNKNOWN) {
    logging.warn(`Attempt to display unknown condition`);
  }
  return CONDITION_TO_DISPLAY_STRING[condition];
}

export interface DefaultSchemaPoliciesConfig {
  admin_policy_id: string;
  create_policy_id: string;
  read_policy_id: string;
  creator_schema_policy_id: string;
  org_members_policy_id?: string;
}

export interface PolicyStatement {
  action: Action;
  condition: Condition;
  itemType: ItemType;
}

export interface Policy {
  id: string;
  name: string;
  isHidden: boolean;
  policyStatements: Array<PolicyStatement>;
  permissions: {writable: boolean};
}

export interface DataPolicy extends Policy {
  isDefault: boolean;
  isAdmin: boolean;
}

export interface SchemaPolicy extends Policy {}

export interface PolicySpec {
  name: string;
  statements: Array<PolicyStatement>;
}

export const DATA_POLICY_SHAPE = PropTypes.shape({
  id: PropTypes.string.isRequired,
  name: PropTypes.string.isRequired,
  isDefault: PropTypes.bool.isRequired,
  isAdmin: PropTypes.bool.isRequired,
});

export const INVALID_ITEM_TYPE_ACTION_TEXT = 'Invalid';

export const ACTION_DESCRIPTIONS_BY_ITEM_TYPE: {[itemType: string]: {[action: string]: string | null}} = {
  [ItemType.BATCH]: {
    [Action.READ]: 'View batches',
    [Action.APPEND]: null,
    [Action.WRITE]: 'Edit batches',
    [Action.ADMIN]: null,
    [Action.CHANGE_REGISTRY_IDENTIFIER]: INVALID_ITEM_TYPE_ACTION_TEXT,
    [Action.CREATE_LOCATION]: INVALID_ITEM_TYPE_ACTION_TEXT,
    [Action.CREATE_REQUEST]: INVALID_ITEM_TYPE_ACTION_TEXT,
    [Action.CREATE_SUBFOLDER]: INVALID_ITEM_TYPE_ACTION_TEXT,
    [Action.EDIT_POLYMER_FEATURES]: INVALID_ITEM_TYPE_ACTION_TEXT,
    [Action.RETRACT_REVIEW]: INVALID_ITEM_TYPE_ACTION_TEXT,
    [Action.UNREGISTER]: INVALID_ITEM_TYPE_ACTION_TEXT,
  },
  [ItemType.BIOENTITY]: {
    [Action.READ]: 'View entities. If condition is IS_AUTHOR, restrict to authors.',
    [Action.APPEND]: 'Create batches for entities. If condition is IS_AUTHOR, restrict to authors.',
    [Action.WRITE]:
      'Edit metadata, sequence bases, protein residues. If condition is IS_AUTHOR, restrict to authors.',
    [Action.ADMIN]: null,
    [Action.CHANGE_REGISTRY_IDENTIFIER]: 'Change registry IDs',
    [Action.CREATE_LOCATION]: INVALID_ITEM_TYPE_ACTION_TEXT,
    [Action.CREATE_REQUEST]: INVALID_ITEM_TYPE_ACTION_TEXT,
    [Action.CREATE_SUBFOLDER]: INVALID_ITEM_TYPE_ACTION_TEXT,
    [Action.EDIT_POLYMER_FEATURES]:
      'Edit annotations, analytics, and overlays. Only applicable for DNA and AA sequences.',
    [Action.RETRACT_REVIEW]: INVALID_ITEM_TYPE_ACTION_TEXT,
    [Action.UNREGISTER]: 'Unregister entities',
  },
  [ItemType.FOLDER_OR_PROJECT]: {
    [Action.READ]: 'View folder / project',
    [Action.APPEND]: 'Add items to folders / projects',
    [Action.WRITE]: 'Edit, move, delete folders',
    [Action.ADMIN]: 'Edit, move, delete projects; Update project settings',
    [Action.CHANGE_REGISTRY_IDENTIFIER]: INVALID_ITEM_TYPE_ACTION_TEXT,
    [Action.CREATE_LOCATION]: INVALID_ITEM_TYPE_ACTION_TEXT,
    [Action.CREATE_REQUEST]: 'Create requests',
    [Action.CREATE_SUBFOLDER]: 'Create folders',
    [Action.EDIT_POLYMER_FEATURES]: INVALID_ITEM_TYPE_ACTION_TEXT,
    [Action.RETRACT_REVIEW]: INVALID_ITEM_TYPE_ACTION_TEXT,
    [Action.UNREGISTER]: INVALID_ITEM_TYPE_ACTION_TEXT,
  },
  [ItemType.REGISTRY]: {
    [Action.READ]: 'View registry',
    [Action.APPEND]: 'Add items to registries',
    [Action.WRITE]: 'Update settings (depending on flag)',
    [Action.ADMIN]: 'Update settings (depending on flag)',
    [Action.CHANGE_REGISTRY_IDENTIFIER]: INVALID_ITEM_TYPE_ACTION_TEXT,
    [Action.CREATE_LOCATION]: 'Create locations',
    [Action.CREATE_REQUEST]: INVALID_ITEM_TYPE_ACTION_TEXT,
    [Action.CREATE_SUBFOLDER]: INVALID_ITEM_TYPE_ACTION_TEXT,
    [Action.EDIT_POLYMER_FEATURES]: INVALID_ITEM_TYPE_ACTION_TEXT,
    [Action.RETRACT_REVIEW]: INVALID_ITEM_TYPE_ACTION_TEXT,
    [Action.UNREGISTER]: INVALID_ITEM_TYPE_ACTION_TEXT,
  },
  [ItemType.LOCATION]: {
    [Action.READ]: 'View locations',
    [Action.APPEND]: 'Create inventory in locations',
    [Action.WRITE]: 'Edit locations',
    [Action.ADMIN]: 'Create locations',
    [Action.CHANGE_REGISTRY_IDENTIFIER]: INVALID_ITEM_TYPE_ACTION_TEXT,
    [Action.CREATE_LOCATION]: INVALID_ITEM_TYPE_ACTION_TEXT,
    [Action.CREATE_REQUEST]: INVALID_ITEM_TYPE_ACTION_TEXT,
    [Action.CREATE_SUBFOLDER]: INVALID_ITEM_TYPE_ACTION_TEXT,
    [Action.EDIT_POLYMER_FEATURES]: INVALID_ITEM_TYPE_ACTION_TEXT,
    [Action.RETRACT_REVIEW]: INVALID_ITEM_TYPE_ACTION_TEXT,
    [Action.UNREGISTER]: INVALID_ITEM_TYPE_ACTION_TEXT,
  },
  [ItemType.NOTEBOOK]: {
    [Action.READ]: 'View entries and stage entries. If condition is IS_AUTHOR, restrict to authors.',
    [Action.APPEND]: null,
    [Action.WRITE]: 'Edit entries and stage entries. If condition is IS_AUTHOR, restrict to authors.',
    [Action.ADMIN]: 'Edit, retract review as non-author',
    [Action.CHANGE_REGISTRY_IDENTIFIER]: INVALID_ITEM_TYPE_ACTION_TEXT,
    [Action.CREATE_LOCATION]: INVALID_ITEM_TYPE_ACTION_TEXT,
    [Action.CREATE_REQUEST]: INVALID_ITEM_TYPE_ACTION_TEXT,
    [Action.CREATE_SUBFOLDER]: INVALID_ITEM_TYPE_ACTION_TEXT,
    [Action.EDIT_POLYMER_FEATURES]: INVALID_ITEM_TYPE_ACTION_TEXT,
    [Action.RETRACT_REVIEW]: 'Retract review',
    [Action.UNREGISTER]: INVALID_ITEM_TYPE_ACTION_TEXT,
  },
  [ItemType.OTHER]: {
    [Action.READ]: 'View items',
    [Action.APPEND]: null,
    [Action.WRITE]: 'Edit items',
    [Action.ADMIN]: 'Template collections: update',
    [Action.CHANGE_REGISTRY_IDENTIFIER]: INVALID_ITEM_TYPE_ACTION_TEXT,
    [Action.CREATE_LOCATION]: INVALID_ITEM_TYPE_ACTION_TEXT,
    [Action.CREATE_REQUEST]: INVALID_ITEM_TYPE_ACTION_TEXT,
    [Action.CREATE_SUBFOLDER]: INVALID_ITEM_TYPE_ACTION_TEXT,
    [Action.EDIT_POLYMER_FEATURES]: INVALID_ITEM_TYPE_ACTION_TEXT,
    [Action.RETRACT_REVIEW]: INVALID_ITEM_TYPE_ACTION_TEXT,
    [Action.UNREGISTER]: INVALID_ITEM_TYPE_ACTION_TEXT,
  },
};

export const CONDITION_DESCRIPTIONS: {[condition: string]: string} = {
  [Condition.ALWAYS]:
    'Always allow. A policy must have a statement with ALWAYS condition and READ action for every item type.',
  [Condition.IS_AUTHOR]: 'Only allow for authors. Only applicable for NOTEBOOK and BIOENTITY item types.',
};

export const ITEM_TYPE_DESCRIPTIONS: {[itemType: string]: string} = {
  [ItemType.BATCH]: 'Batches',
  [ItemType.BIOENTITY]: 'Entities (DNA sequences, AA sequences, custom entities)',
  [ItemType.FOLDER_OR_PROJECT]: 'Folders, projects, and registries',
  [ItemType.LOCATION]: 'Locations',
  [ItemType.NOTEBOOK]: 'Entries and stage entries',
  [ItemType.OTHER]: 'All other item types (e.g. protocols, feature libraries, template collections)',
};

export interface ServerSchemaPolicyCollaborator {
  id: string;
  schemaPolicyId?: string;
  adminSchemaPolicyId?: string | null;
  memberSchemaPolicyId?: string | null;
}

const ACTION_OPTION_FRAGMENT = gql`
  fragment ActionOption on ActionOptionNode {
    action
    name
    description
    disabled
    itemTypeOverride
    conditionOptions {
      condition
      name
    }
  }
`;

export const POLICY_TEMPLATE_FRAGMENT = gql`
  fragment PolicyTemplate on PolicyTemplateNode {
    itemTypeOptions {
      itemType
      name
      description
      actionOptions {
        ... on ActionOptionNode {
          ...ActionOption
        }
        ... on ActionOptionGroupNode {
          name
          description
          actionOptions {
            ...ActionOption
          }
        }
      }
    }
  }
  ${ACTION_OPTION_FRAGMENT}
`;
